import { Facility, Vector2 } from "@app/shared";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { MapContext, useFacilityCoordsHelper } from "../../../helpers";
import { MapShape } from "../maps/MapShape";
import { AlignOriginHandle } from "../maps/MapShapes/AlignOriginHandle";
import { LaneLineMapShape } from "../maps/MapShapes/LaneLineMapShape";
import { DeviceAlignmentState } from "./hooks/useDeviceAlignment";

/** How far (meters) the rotate handle sits from the device, along its heading. */
const ROTATE_HANDLE_DISTANCE_METERS = 10;

type AlignRotateHandleProps = {
    position: google.maps.LatLngLiteral;
    onDrag: (latLng: google.maps.LatLng) => void;
    onDragEnd: (latLng: google.maps.LatLng) => void;
};

/** Orange draggable handle used to rotate the rig around the device. */
const AlignRotateHandle: React.FC<AlignRotateHandleProps> = ({ position, onDrag, onDragEnd }) => {
    const [parentMap] = useContext(MapContext);
    const marker = useRef<google.maps.marker.AdvancedMarkerElement>();
    const onDragRef = useRef(onDrag);
    onDragRef.current = onDrag;
    const onDragEndRef = useRef(onDragEnd);
    onDragEndRef.current = onDragEnd;

    useEffect(() => {
        if (!parentMap) return;

        const el = document.createElement("div");
        el.style.width = "22px";
        el.style.height = "22px";
        el.style.borderRadius = "50%";
        el.style.backgroundColor = "rgba(249, 115, 22, 0.95)";
        el.style.border = "3px solid #ffffff";
        el.style.boxShadow = "0 0 0 2px rgba(249, 115, 22, 0.5)";
        el.style.cursor = "grab";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.color = "#ffffff";
        el.style.fontSize = "13px";
        el.style.fontWeight = "bold";
        el.textContent = "⟳";
        el.title = "Drag to rotate";

        const m = new google.maps.marker.AdvancedMarkerElement({
            position,
            map: parentMap,
            content: el,
            gmpDraggable: true,
            zIndex: 10001,
        });
        marker.current = m;

        const startL = m.addListener("dragstart", () => {
            el.style.cursor = "grabbing";
        });
        const dragL = m.addListener("drag", (e: google.maps.MapMouseEvent) => {
            if (e.latLng) onDragRef.current(e.latLng);
        });
        const endL = m.addListener("dragend", (e: google.maps.MapMouseEvent) => {
            el.style.cursor = "grab";
            if (e.latLng) onDragEndRef.current(e.latLng);
        });

        return () => {
            startL.remove();
            dragL.remove();
            endL.remove();
            m.map = null;
            marker.current = undefined;
        };
    }, [parentMap]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (marker.current) marker.current.position = position;
    }, [position.lat, position.lng]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

type DeviceAlignmentLayerProps = {
    facility: Facility;
    alignment: DeviceAlignmentState;
    /** Show the recent-traffic polylines (they can crowd busy sites — toggleable from the chip) */
    showTracks: boolean;
    showDetectionZoneLabels: boolean;
    showScreenLineLabels: boolean;
    showLaneLineLabels: boolean;
};

/**
 * Map overlay for "align device" mode: the device's rig (its lane lines, device-anchored
 * shapes, and recent tracks) rendered at the candidate pose, with a translate handle on the
 * device and a rotate handle on its heading. Ghosted lane lines mark the original position.
 */
export const DeviceAlignmentLayer: React.FC<DeviceAlignmentLayerProps> = ({ facility, alignment, showTracks, showDetectionZoneLabels, showScreenLineLabels, showLaneLineLabels }) => {
    const coordsHelper = useFacilityCoordsHelper(facility);
    const rafRef = useRef<number>();

    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    // Coalesce drag events to one pose update per animation frame — every update reprojects the whole rig.
    const throttle = useCallback((apply: () => void) => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = undefined;
            apply();
        });
    }, []);

    const { candidatePose } = alignment;

    const applyTranslate = useCallback(
        (latLng: google.maps.LatLng) => {
            const meters = coordsHelper.fromLatLng(latLng);
            alignment.setCandidatePosition(meters.x, meters.y);
        },
        [coordsHelper, alignment],
    );

    const headingFromLatLng = useCallback(
        (latLng: google.maps.LatLng): number => {
            if (!candidatePose) return 0;
            const meters = coordsHelper.fromLatLng(latLng);
            // Compass heading from the device to the cursor (facility frame: x east, y south).
            return (Math.atan2(meters.x - candidatePose.x, -(meters.y - candidatePose.y)) * 180) / Math.PI;
        },
        [coordsHelper, candidatePose],
    );

    const handleTranslateDrag = useCallback((latLng: google.maps.LatLng) => throttle(() => applyTranslate(latLng)), [throttle, applyTranslate]);
    const handleTranslateDragEnd = useCallback((latLng: google.maps.LatLng) => applyTranslate(latLng), [applyTranslate]);
    const handleRotateDrag = useCallback((latLng: google.maps.LatLng) => throttle(() => alignment.setCandidateHeading(headingFromLatLng(latLng))), [throttle, alignment, headingFromLatLng]);
    const handleRotateDragEnd = useCallback((latLng: google.maps.LatLng) => alignment.setCandidateHeading(headingFromLatLng(latLng)), [alignment, headingFromLatLng]);

    if (!alignment.active || !candidatePose) return null;

    const deviceLatLng = coordsHelper.toLatLng(new Vector2({ x: candidatePose.x, y: candidatePose.y }));
    const devicePosition = { lat: deviceLatLng.lat(), lng: deviceLatLng.lng() };
    const headingRad = (candidatePose.headingDegrees * Math.PI) / 180;
    const rotateHandleMeters = new Vector2({
        x: candidatePose.x + Math.sin(headingRad) * ROTATE_HANDLE_DISTANCE_METERS,
        y: candidatePose.y - Math.cos(headingRad) * ROTATE_HANDLE_DISTANCE_METERS,
    });
    const rotateLatLng = coordsHelper.toLatLng(rotateHandleMeters);
    const rotateHandlePosition = { lat: rotateLatLng.lat(), lng: rotateLatLng.lng() };

    return (
        <>
            {alignment.ghostLaneLines.map((line) => (
                <LaneLineMapShape key={line.id} facility={facility} shape={line} opacity={0.45} />
            ))}
            {showTracks &&
                alignment.trackLines.map((line) => (
                    <LaneLineMapShape key={line.id} facility={facility} shape={line} opacity={0.8} cased />
                ))}
            {alignment.alignedRigShapes.map((shape) => (
                <MapShape key={shape.id} shape={shape} facility={facility} showDetectionZoneLabels={showDetectionZoneLabels} showLaneLineLabels={showLaneLineLabels} showScreenLineLabels={showScreenLineLabels} />
            ))}
            {alignment.alignedDevice && <MapShape shape={alignment.alignedDevice} facility={facility} />}
            <AlignOriginHandle position={devicePosition} onDrag={handleTranslateDrag} onDragEnd={handleTranslateDragEnd} />
            <AlignRotateHandle position={rotateHandlePosition} onDrag={handleRotateDrag} onDragEnd={handleRotateDragEnd} />
        </>
    );
};
